import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { alpha, styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ButtonBase, Tooltip } from '@mui/material';
import Iconify from './Iconify';
import { RED } from '../utils/brandColors';
import { fetchPendingRequestCount } from '../redux/actions/socialActions';

// ----------------------------------------------------------------------

const ListItemStyle = styled(ButtonBase)(({ theme }) => ({
  height: 44,
  width: '100%',
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: theme.spacing(0, 1.5, 0, 1.75),
  marginBottom: 4,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    backgroundColor: alpha(RED, 0.06),
    color: theme.palette.text.primary,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  minWidth: 'auto',
  marginRight: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s ease',
});

const ActiveIndicator = styled(Box)({
  position: 'absolute',
  left: 5,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 4,
  height: 20,
  borderRadius: 4,
  backgroundColor: RED,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
});

// ----------------------------------------------------------------------

function NavItem({ item, active, mini = false }) {
  const { title, path, icon, children, badgeKey } = item;
  const [open, setOpen] = useState(false);
  const isActive = active(path);
  // Badge counts are resolved here rather than baked into NavConfig, because
  // NavConfig is a pure function of userInfo and must not subscribe to redux.
  const followRequests = useSelector((s) => s.social?.pendingRequests || 0);
  // DMs badge on CONVERSATIONS, not messages: one thread needing a reply is
  // one badge, however many messages it holds.
  const dmUnread = useSelector((s) => s.social?.dmUnread?.conversations || 0);
  const badgeCount = (() => {
    if (badgeKey === 'followRequests') return followRequests;
    if (badgeKey === 'dmUnread') return dmUnread;
    return 0;
  })();

  const activeRootStyle = {
    color: RED,
    fontWeight: 700,
    backgroundColor: alpha(RED, 0.08),
    paddingLeft: mini ? 0 : '18px',
    '&:hover': {
      backgroundColor: alpha(RED, 0.12),
    },
  };

  const activeIconStyle = {
    color: RED,
    transform: 'scale(1.12)',
  };

  if (children && !mini) {
    return (
      <>
        <ListItemStyle
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            ...(isActive && activeRootStyle),
          }}
        >
          {isActive && <ActiveIndicator />}
          <ListItemIconStyle sx={{ ...(isActive && activeIconStyle) }}>
            {icon && (typeof icon === 'function' ? icon(isActive) : icon)}
          </ListItemIconStyle>
          <ListItemText
            disableTypography
            primary={title}
            sx={{ fontSize: '0.95rem', fontWeight: isActive ? 700 : 600 }}
          />
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child) => {
              const childActive = active(child.path);
              return (
                <ListItemStyle
                  key={child.title}
                  component={RouterLink}
                  to={child.path}
                  sx={{
                    pl: 4,
                    ...(childActive && activeRootStyle),
                  }}
                >
                  <ListItemIconStyle sx={{ ...(childActive && activeIconStyle) }}>
                    <Box
                      component="span"
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: childActive ? RED : 'text.disabled',
                        transition: 'transform 200ms',
                        ...(childActive && { transform: 'scale(1.6)' }),
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText
                    disableTypography
                    primary={child.title}
                    sx={{ fontSize: '0.9rem', fontWeight: childActive ? 700 : 600 }}
                  />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  const renderItem = (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActive && activeRootStyle),
        ...(mini && { justifyContent: 'center', px: 0 }),
      }}
    >
      {isActive && <ActiveIndicator />}
      <ListItemIconStyle sx={{ ...(isActive && activeIconStyle), ...(mini && { marginRight: 0 }) }}>
        {icon && (typeof icon === 'function' ? icon(isActive) : icon)}
      </ListItemIconStyle>
      {!mini && (
        <ListItemText
          disableTypography
          primary={title}
          sx={{ fontSize: '0.95rem', fontWeight: isActive ? 700 : 600 }}
        />
      )}
      {badgeCount > 0 && (
        <Box
          data-testid={`nav-badge-${badgeKey}`}
          sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            mr: mini ? 0 : 1,
            borderRadius: '50px',
            bgcolor: RED,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(mini && { position: 'absolute', top: 6, right: 6 }),
          }}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </Box>
      )}
    </ListItemStyle>
  );

  if (mini) {
    return (
      <Tooltip title={title} placement="right" arrow>
        {renderItem}
      </Tooltip>
    );
  }

  return renderItem;
}

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  mini: PropTypes.bool,
};

// ----------------------------------------------------------------------

export default function NavSection({ navConfig, mini = false, ...other }) {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const userInfo = useSelector((s) => s.userLogin?.userInfo);
  const needsBadge = navConfig.some((i) => i.badgeKey === 'followRequests');

  // Populate the follow-request badge.
  //
  // This deliberately lives HERE, in the component that renders the badge,
  // rather than in NotificationBell. Dispatching from the bell re-rendered the
  // whole dashboard layout while a route transition was in flight, which
  // detached list rows mid-click - it surfaced as a Playwright "element was
  // detached from the DOM, retrying" timeout in the studio spec rather than as
  // anything that looked like a state bug. Keeping the fetch co-located with
  // its consumer means only the nav re-renders.
  useEffect(() => {
    if (!userInfo || userInfo.isAnon || !needsBadge) return;
    dispatch(fetchPendingRequestCount());
    // Deliberately keyed on the token, not the userInfo object: unrelated
    // profile updates must not refetch.
  }, [dispatch, userInfo?.access, userInfo?.isAnon, needsBadge]); // eslint-disable-line react-hooks/exhaustive-deps

  const match = (path) => {
    if (!path) return false;
    // Explore nav points at "/", which also matches matchPath end:false on
    // any nested route - special-case it so only the bare root counts as
    // active for that entry. (Legacy /dashboard/app still resolves via the
    // SPA redirect in routes.js, but we don't carry that path in nav.)
    if (path === '/') {
      return pathname === '/';
    }
    return !!matchPath({ path, end: false }, pathname);
  };

  return (
    <Box {...other}>
      <List disablePadding sx={{ px: mini ? 0.75 : 1.5 }}>
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} mini={mini} />
        ))}
      </List>
    </Box>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  mini: PropTypes.bool,
};
